import type * as THREE from 'three'
import type { ViewportRect } from '@/shared/types/core'
import type { ViewportCamera, ViewRegistry } from '@/shared/types/services'
import type { SpatialSurfaceScreenQuery } from '@/shared/types/spatial'
import type { ViewId, ViewSnapshot } from '@/shared/types/view'
import { ndcToRay, screenToNDC, toViewportLocalPoint } from '@/shared/utils'

/** 屏幕坐标解析后的射线上下文 */
export interface ResolvedScreenRayContext {
  /** 用于产生射线的相机 */
  camera: THREE.Camera
  /** 世界空间射线 */
  ray: THREE.Ray
  /** 归一化后的屏幕坐标，单位像素 */
  screen: THREE.Vector2
  /** 容器视口矩形 */
  viewport: ViewportRect
  /** 命中视图 ID；多视口时有效 */
  viewId?: ViewId
  /** 命中视图的局部矩形 */
  viewRect?: ViewportRect
  /** 视图 layer 掩码 */
  layerMask?: number
}

/**
 * 根据屏幕坐标解析出世界空间射线，兼容单视口与多视口场景。
 */
export class ViewRayResolver {
  constructor(
    private readonly getViewport: () => ViewportRect,
    private readonly getViewRegistry?: () => ViewRegistry | null | undefined
  ) {}

  /**
   * 解析屏幕查询对应的射线上下文。
   *
   * @param query - 屏幕空间表面查询
   * @returns 上下文，含命中视图与射线；解析失败返回 null
   */
  resolve(query: SpatialSurfaceScreenQuery): ResolvedScreenRayContext | null {
    const containerViewport = cloneRect(this.getViewport())
    const screenInContainer = this.normalizeScreen(query.screen, query, containerViewport)
    const context = this.resolveContext(screenInContainer, query.camera, query, containerViewport)

    if (context.viewport.width <= 0 || context.viewport.height <= 0) {
      return null
    }

    context.camera.updateMatrixWorld(true)
    const ndc = screenToNDC(screenInContainer, context.viewport)
    return {
      camera: context.camera,
      ray: ndcToRay(ndc, context.camera),
      screen: screenInContainer,
      viewport: cloneRect(context.viewport),
      viewId: context.viewId,
      viewRect: context.viewRect ? cloneRect(context.viewRect) : undefined,
      layerMask: context.layerMask,
    }
  }

  private resolveContext(
    screen: THREE.Vector2,
    fallbackCamera: THREE.Camera,
    options: Partial<SpatialSurfaceScreenQuery>,
    containerViewport: ViewportRect
  ): {
    camera: THREE.Camera
    viewport: ViewportRect
    viewId?: ViewId
    viewRect?: ViewportRect
    layerMask?: number
  } {
    const registry = this.getViewRegistry?.() ?? null
    const resolvedView = this.resolveViewSnapshot(registry, screen, options.viewId)

    if (options.viewId && registry && !resolvedView) {
      return {
        camera: fallbackCamera,
        viewport: { x: 0, y: 0, width: 0, height: 0 },
        viewId: options.viewId,
      }
    }

    const explicitViewRect = options.viewRect ?? options.viewport
    const viewRect = resolvedView
      ? cloneRect(resolvedView.rect)
      : explicitViewRect
        ? cloneRect(explicitViewRect)
        : undefined

    return {
      camera: this.resolveCamera(registry, resolvedView?.id, fallbackCamera),
      viewport: viewRect ?? {
        x: 0,
        y: 0,
        width: containerViewport.width,
        height: containerViewport.height,
      },
      viewId: resolvedView?.id ?? options.viewId,
      viewRect,
      layerMask: resolvedView?.layerMask ?? options.layerMask,
    }
  }

  private resolveViewSnapshot(
    registry: ViewRegistry | null,
    screen: THREE.Vector2,
    requestedViewId?: ViewId
  ): ViewSnapshot | null {
    if (!registry) {
      return null
    }

    if (requestedViewId) {
      const requestedView = registry.get(requestedViewId)
      return this.isQueryableView(requestedView) ? requestedView : null
    }

    const hitView = registry.resolveViewAtScreen(screen)
    if (this.isQueryableView(hitView)) {
      return hitView
    }

    const activeView = registry.getActive()
    return this.isQueryableView(activeView) ? activeView : null
  }

  private resolveCamera(
    registry: ViewRegistry | null,
    viewId: ViewId | undefined,
    fallbackCamera: THREE.Camera
  ): THREE.Camera {
    if (!registry || !viewId) {
      return fallbackCamera
    }

    const managedCamera = registry.getExtension(viewId, 'viewportCamera') as
      | ViewportCamera
      | undefined
    return managedCamera?.getActiveCamera() ?? fallbackCamera
  }

  private isQueryableView(view: ViewSnapshot | undefined): view is ViewSnapshot {
    return !!view && view.visible && view.rect.width > 0 && view.rect.height > 0
  }

  private normalizeScreen(
    screen: THREE.Vector2,
    options: Partial<SpatialSurfaceScreenQuery>,
    containerViewport: ViewportRect
  ): THREE.Vector2 {
    if (options.viewId || options.viewRect || options.viewport) {
      return screen.clone()
    }

    return toViewportLocalPoint(screen, containerViewport)
  }
}

function cloneRect(rect: { x: number; y: number; width: number; height: number }): ViewportRect {
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  }
}
